import { useEffect, useRef, useState } from "react";

import { useInView } from "react-intersection-observer";
import { Channel } from "stream-chat";

import { getChatClient } from "@/components/pieces/chat/ChatContext";

export function useUnreadMessages({
  showMessageCount,
  chatChannelId,
}: {
  showMessageCount: boolean;
  chatChannelId: string;
}): {
  hasMessages: boolean;
  unreadMessageCount: number;
  containerRef: (node?: Element | null) => void;
  inView: boolean;
} {
  const [hasMessages, setHasMessages] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  const mountedRef = useRef(false);
  const chatChannelRef = useRef<Channel>(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
  });

  const updateDataFromChatChannel = () => {
    if (!mountedRef.current || !chatChannelRef.current) {
      return;
    }

    setHasMessages(!!chatChannelRef.current.data?.last_message_at);
    setUnreadMessageCount(chatChannelRef.current.countUnread());
  };

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (chatChannelRef.current) {
        chatChannelRef.current.off(updateDataFromChatChannel);
      }
    };
  }, []);

  useEffect(() => {
    if (!showMessageCount) {
      return;
    }

    if (inView) {
      async function loadMessageCount() {
        const chatClient = getChatClient();
        chatChannelRef.current = chatClient.channel("messaging", chatChannelId);
        await chatChannelRef.current.watch();
        if (mountedRef.current) {
          updateDataFromChatChannel();
          chatChannelRef.current.on(updateDataFromChatChannel);
        }
      }
      loadMessageCount().catch(() => null);
    }
  }, [inView, showMessageCount, chatChannelId]);

  return {
    hasMessages,
    unreadMessageCount,
    containerRef: ref,
    inView,
  };
}
